import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {EligibleOfferSetModel} from '../../../../../model/eligible-offer-set.model';
import {OfferRuleService} from '../../../../../service/offer-rule.service';
import {VehicleService} from '../../../../../service/vehicle.service';
import {MakeModel} from '../../../../../model/make.model';
import {VehicleModel} from '../../../../../model/vehicle-model.model';
import {OfferRegistryProcessorSyncService} from '../../../../../service/offer-registry-processor-sync.service';

@Component({
  selector: 'auto-offer-set-rule-modal',
  templateUrl: './offer-set-rule-modal.component.html',
  styleUrls: ['./offer-set-rule-modal.component.scss'],
  standalone: false
})
export class OfferSetRuleModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() dialogTitle = '';
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() rule: OfferRuleModel | null = null;
  @Input() accountId: number;
  @Input() selectedRuleType = 'offerSetRule';
  @Input() eligibleOfferSets: EligibleOfferSetModel[] = [];
  @Input() offerSetOfferTextMap: {[key: number]: string} = {};

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  ruleForm: FormGroup;
  ruleTypeOptions = [
    {label: 'Offer Set Rule', value: 'offerSetRule'},
    {label: 'Dollar Discount Rule', value: 'discountRule'}
  ];
  filteredRuleTypeOptions = [...this.ruleTypeOptions];
  publicOfferSetTooltip = 'Dollar discount rules are applicable to private offer sets';
  privacyOptions = [
    {label: 'Public', value: false},
    {label: 'Private', value: true}
  ];
  conditionOptions = [
    {label: 'New', value: 'New'},
    {label: 'Used', value: 'Used'},
    {label: 'CPO', value: 'CPO'},
    {label: 'Any', value: 'Any'}
  ];

  makes: MakeModel[] = [];
  models: VehicleModel[] = [];
  filteredModels: VehicleModel[] = [];
  filteredEligibleOfferSets: EligibleOfferSetModel[] = [];
  anyMakeOption: MakeModel;
  anyModelOption: VehicleModel;
  modelDropdownDisabled = true;
  savingRule = false;
  selectedRuleId: number | null = null;
  selectedMakeId: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private offerRuleService: OfferRuleService,
    private vehicleService: VehicleService,
    private offerRegistryProcessorSyncService: OfferRegistryProcessorSyncService
  ) {
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeAnyOptions();
    this.loadMakes();
    this.loadModels();
    this.updateRuleTypeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const visibilityChange = changes['visible'];
    if (visibilityChange) {
      this.handleVisibilityChange();
    }
    const ruleChange = changes['rule'];
    if (ruleChange && this.visible) {
      this.applyRuleToForm();
    }
  }

  onRuleTypeChange(): void {
    const ruleTypeControl = this.ruleForm.get('ruleType');
    const offerSetControl = this.ruleForm.get('offerSetId');
    const discountControl = this.ruleForm.get('dollarAmountDiscount');
    let isDiscountRule = false;
    if (ruleTypeControl) {
      const ruleTypeValue = ruleTypeControl.value;
      isDiscountRule = ruleTypeValue === 'discountRule';
    }
    if (offerSetControl) {
      if (isDiscountRule) {
        offerSetControl.clearValidators();
        offerSetControl.setValue(null);
        offerSetControl.disable({emitEvent: false});
      } else {
        offerSetControl.setValidators([Validators.required]);
        offerSetControl.enable({emitEvent: false});
      }
      offerSetControl.updateValueAndValidity();
    }
    if (discountControl) {
      if (isDiscountRule) {
        discountControl.setValidators([Validators.required, Validators.min(0.01)]);
        discountControl.enable({emitEvent: false});
      } else {
        discountControl.setValidators([Validators.min(0.01)]);
        discountControl.setValue(null);
        discountControl.disable({emitEvent: false});
      }
      discountControl.updateValueAndValidity();
    }
  }

  onOfferSetChange(offerSetId: number): void {
    const matchingOfferSet = this.eligibleOfferSets.find((offerSet) => offerSet.id === offerSetId);
    const hasMatch = matchingOfferSet !== undefined;
    if (hasMatch && matchingOfferSet) {
      const offerSetPatch = this.buildOfferSetPatch(matchingOfferSet);
      this.ruleForm.patchValue(offerSetPatch);
      this.applyMakeAndModelSelectionFromForm();
    }
    this.applyPublicOfferSetRestrictions();
  }

  onPrivacySelectionChange(): void {
    this.applyPublicOfferSetRestrictions();
    this.onRuleTypeChange();
  }

  onMakeSelectionChange(makeId: number | null): void {
    this.filterModelsForMake(makeId);
    const makeName = this.getMakeNameById(makeId);
    this.ruleForm.patchValue({
      make: makeName,
      model: '',
      modelId: null
    });
  }

  onModelSelectionChange(modelId: number | null): void {
    let modelName = '';
    const hasValue = modelId !== null;
    if (hasValue) {
      const match = this.filteredModels.find((model) => model.id === modelId);
      if (match) {
        modelName = match.name;
      }
    }
    this.ruleForm.patchValue({
      model: modelName
    });
  }

  saveRule(): void {
    const formValid = this.isFormValidForSave();
    if (formValid) {
      const payload = this.buildPayloadFromForm();
      this.submitRule(payload);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please correct the highlighted fields before saving.'
      });
    }
  }

  closeDialog(): void {
    this.savingRule = false;
    this.closed.emit();
  }

  private handleVisibilityChange(): void {
    if (this.visible) {
      this.applyRuleToForm();
    }
    if (!this.visible) {
      this.resetState();
    }
  }

  private applyRuleToForm(): void {
    if (this.mode === 'edit' && this.rule) {
      this.setFormForEdit(this.rule);
    } else {
      this.setDefaultFormValues();
    }
    this.applyPublicOfferSetRestrictions();
    this.onRuleTypeChange();
  }

  private initializeForm(): void {
    this.ruleForm = this.formBuilder.group({
      ruleType: ['offerSetRule', Validators.required],
      status: [true, Validators.required],
      isPrivate: [true],
      offerSetId: [null],
      year: [null],
      make: [''],
      makeId: [null],
      model: [''],
      modelId: [null],
      trim: [''],
      maxSalePrice: [null, Validators.min(0)],
      vehicleLimit: [null, Validators.min(0)],
      vehicleCondition: ['New', Validators.required],
      dollarAmountDiscount: [null, Validators.min(0.01)]
    });
  }

  private initializeAnyOptions(): void {
    this.anyMakeOption = new MakeModel();
    this.anyMakeOption.id = null;
    this.anyMakeOption.displayName = 'Any';
    this.anyMakeOption.active = true;
    this.anyModelOption = new VehicleModel();
    this.anyModelOption.id = null;
    this.anyModelOption.makeId = null;
    this.anyModelOption.name = 'Any';
    this.anyModelOption.active = true;
  }

  private loadMakes(): void {
    this.vehicleService.getAllMakes().subscribe({
      next: (makes) => {
        this.makes = [this.anyMakeOption, ...makes];
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load makes.'
        });
      }
    });
  }

  private loadModels(): void {
    this.vehicleService.getAllModels().subscribe({
      next: (models) => {
        this.models = models;
        this.filteredModels = [this.anyModelOption, ...models];
        this.applyMakeAndModelSelectionFromForm();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load models.'
        });
      }
    });
  }

  private setDefaultFormValues(): void {
    this.selectedRuleId = null;
    this.ruleForm.setValue({
      ruleType: this.selectedRuleType,
      status: true,
      isPrivate: true,
      offerSetId: null,
      year: null,
      make: '',
      makeId: null,
      model: '',
      modelId: null,
      trim: '',
      maxSalePrice: null,
      vehicleLimit: null,
      vehicleCondition: 'New',
      dollarAmountDiscount: null
    });
    this.selectedMakeId = null;
    this.filterModelsForMake(null);
    this.updateRuleTypeOptions();
  }

  private setFormForEdit(rule: OfferRuleModel): void {
    this.selectedRuleId = null;
    if (rule.id !== undefined && rule.id !== null) {
      this.selectedRuleId = rule.id;
    }
    const formValues = this.buildEditFormValues(rule);
    this.ruleForm.setValue(formValues);
    this.applyMakeAndModelSelectionFromForm();
    this.updateRuleTypeOptions();
  }

  private buildEditFormValues(rule: OfferRuleModel): Record<string, unknown> {
    const ruleType = this.getRuleTypeFromRule(rule);
    const conditionValue = this.normalizeConditionValue(rule.vehicleCondition);
    const offerSetValue = this.resolveRuleNumber(rule.offerSetId);
    const yearValue = this.resolveRuleNumber(rule.year);
    const maxSalePriceValue = this.resolveRuleNumber(rule.maxSalePrice);
    const vehicleLimitValue = this.resolveRuleNumber(rule.vehicleLimit);
    const discountValue = this.resolveRuleNumber(rule.dollarAmountDiscount);
    const isPrivateValue = this.resolveRuleBoolean(rule.isPrivate);
    const makeValue = this.resolveRuleString(rule.make);
    const modelValue = this.resolveRuleString(rule.model);
    const trimValue = this.resolveRuleString(rule.trim);
    const makeIdValue = this.findMakeIdByName(makeValue);
    let modelIdValue: number | null = null;
    if (makeIdValue !== null) {
      modelIdValue = this.findModelIdByNameAndMake(modelValue, makeIdValue);
    }
    const statusValue = this.resolveStatusValue(rule.status);
    return {
      ruleType,
      status: statusValue,
      isPrivate: isPrivateValue,
      offerSetId: offerSetValue,
      year: yearValue,
      make: makeValue,
      makeId: makeIdValue,
      model: modelValue,
      modelId: modelIdValue,
      trim: trimValue,
      maxSalePrice: maxSalePriceValue,
      vehicleLimit: vehicleLimitValue,
      vehicleCondition: conditionValue,
      dollarAmountDiscount: discountValue
    };
  }

  private isFormValidForSave(): boolean {
    let isValid = true;
    if (!this.ruleForm.valid) {
      isValid = false;
    }
    const discountRule = this.isDiscountRule();
    const discountControl = this.ruleForm.get('dollarAmountDiscount');
    if (discountRule && discountControl) {
      const discountValue = discountControl.value;
      const hasDiscount = discountValue !== null && discountValue !== undefined && discountValue !== '';
      if (!hasDiscount) {
        isValid = false;
      }
      if (hasDiscount && discountValue <= 0) {
        isValid = false;
      }
    }
    const offerSetControl = this.ruleForm.get('offerSetId');
    if (!discountRule && offerSetControl) {
      const offerSetValue = offerSetControl.value;
      const hasOfferSet = offerSetValue !== null && offerSetValue !== undefined && offerSetValue !== '';
      if (!hasOfferSet) {
        isValid = false;
      }
    }
    return isValid;
  }

  private buildPayloadFromForm(): OfferRuleModel {
    const payload = new OfferRuleModel();
    payload.accountId = this.accountId;
    payload.offerSetId = this.assignNumberFromControl('offerSetId') ?? null;
    payload.year = this.assignNumberFromControl('year') ?? null;
    payload.maxSalePrice = this.assignNumberFromControl('maxSalePrice') ?? null;
    payload.vehicleLimit = this.assignNumberFromControl('vehicleLimit') ?? null;
    payload.dollarAmountDiscount = this.assignNumberFromControl('dollarAmountDiscount') ?? null;
    payload.isPrivate = this.assignBooleanFromControl('isPrivate');
    payload.make = this.assignStringFromControl('make');
    payload.model = this.assignStringFromControl('model');
    payload.trim = this.assignStringFromControl('trim');
    payload.vehicleCondition = this.assignCondition();
    payload.status = this.resolveStatusForPayload();
    if (this.selectedRuleId !== null) {
      payload.id = this.selectedRuleId;
    }
    return payload;
  }

  private assignCondition(): string | undefined {
    const conditionControl = this.ruleForm.get('vehicleCondition');
    let condition: string | undefined;
    if (conditionControl) {
      const value = conditionControl.value;
      if (value) {
        condition = value;
      }
    }
    return condition;
  }

  private assignNumberFromControl(controlName: string): number | undefined {
    let numberValue: number | undefined;
    const control = this.ruleForm.get(controlName);
    if (control) {
      const rawValue = control.value;
      const hasValue = rawValue !== null && rawValue !== undefined && rawValue !== '';
      if (hasValue) {
        const parsedValue = Number(rawValue);
        const isValidNumber = !Number.isNaN(parsedValue);
        const isPristineZero = parsedValue === 0 && control.pristine;
        if (isValidNumber && !isPristineZero) {
          numberValue = parsedValue;
        }
      }
    }
    return numberValue;
  }

  private assignBooleanFromControl(controlName: string): boolean | undefined {
    let booleanValue: boolean | undefined;
    const control = this.ruleForm.get(controlName);
    if (control) {
      booleanValue = control.value;
    }
    return booleanValue;
  }

  private assignStringFromControl(controlName: string): string | null | undefined {
    let stringValue: string | null | undefined;
    const control = this.ruleForm.get(controlName);
    if (control) {
      const rawValue = control.value;
      const hasValue = rawValue !== undefined && rawValue !== null;
      if (hasValue) {
        if (rawValue === '') {
          stringValue = null;
        } else {
          stringValue = rawValue;
        }
      }
    }
    return stringValue;
  }

  private resolveStatusForPayload(): string | undefined {
    let statusValue: string | undefined;
    const statusControl = this.ruleForm.get('status');
    if (statusControl) {
      const controlValue = statusControl.value;
      let isActive = false;
      if (controlValue === true) {
        isActive = true;
      }
      if (controlValue === 'Active') {
        isActive = true;
      }
      if (isActive) {
        statusValue = 'Active';
      } else {
        statusValue = 'Inactive';
      }
    }
    return statusValue;
  }

  private resolveStatusValue(status: string | boolean | undefined): boolean {
    let isActive = false;
    if (status === true) {
      isActive = true;
    }
    if (status === 'Active') {
      isActive = true;
    }
    return isActive;
  }

  private resolveRuleNumber(value: number | undefined): number | null {
    let resolvedValue: number | null = null;
    if (value !== null && value !== undefined) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  private resolveRuleString(value: string | undefined): string {
    let resolvedValue = '';
    if (value) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  /**
   * Resolves boolean value
   * @param value
   * @private
   */
  private resolveRuleBoolean(value: boolean | undefined): boolean {
    let resolvedValue = false;
    if (value !== undefined) {
      resolvedValue = value;
    }

    return resolvedValue;
  }

  private submitRule(payload: OfferRuleModel): void {
    const isUpdate = this.selectedRuleId !== null;
    this.savingRule = true;
    if (isUpdate && this.selectedRuleId !== null) {
      this.offerRuleService.updateOfferRule(this.selectedRuleId, payload).subscribe({
        next: () => {
          this.handleSaveSuccess('Rule updated.');
        },
        error: () => {
          this.handleSaveError('Failed to update rule.');
        }
      });
    } else {
      this.offerRuleService.createOfferRule(payload).subscribe({
        next: () => {
          this.handleSaveSuccess('Rule created.');
        },
        error: () => {
          this.handleSaveError('Failed to create rule.');
        }
      });
    }
  }

  private handleSaveSuccess(detail: string): void {
    this.savingRule = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail
    });
    this.triggerPrivateOfferDataProcessing();
    this.saved.emit();
  }

  private handleSaveError(detail: string): void {
    this.savingRule = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail
    });
  }

  /**
   * Trigger private offer data processing for all standard offer sets.
   */
  triggerPrivateOfferDataProcessing() {
    this.offerRegistryProcessorSyncService.processForAllStandardOfferSets(this.accountId).subscribe({
      next: () => {
        this.offerRegistryProcessorSyncService.syncForAllStandardOfferSets(this.accountId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Rule saved, and private offer data sync triggered.'
            });
          },
        })
      },
      error: () => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Rule saved, but private offer data sync could not be triggered.'
        });
      }
    });
  }

  private resetState(): void {
    this.selectedRuleId = null;
    this.savingRule = false;
  }

  isDiscountRule(): boolean {
    let isDiscount = false;
    const ruleType = this.ruleForm.get('ruleType');
    if (ruleType) {
      isDiscount = ruleType.value === 'discountRule';
    }
    return isDiscount;
  }

  /**
   * Determines if a Public Offer Set is selected
   */
  isPublicOfferSetSelected(): boolean {
    let isPublic = false;
    const offerSetId = this.ruleForm.get('offerSetId');
    if (offerSetId) {
      const value = offerSetId.value;
      const hasValue = value !== null && value !== undefined;
      if (hasValue) {
        const match = this.eligibleOfferSets.find((offerSet) => offerSet.id === value);
        if (match && !this.resolveOfferSetIsPrivate(match)) {
          isPublic = true;
        }
      }
    }
    return isPublic;
  }

  /**
   * Determines if a Public Rule is selected during creation
   */
  isPublicRuleSelection(): boolean {
    let isPublic = false;
    const privacyValue = this.getPrivacySelectionValue();
    if (privacyValue === false) {
      isPublic = true;
    }
    if (!isPublic && privacyValue === null) {
      if (this.isPublicOfferSetSelected()) {
        isPublic = true;
      }
    }
    return isPublic;
  }

  /**
   * Applies Public Offer Set restrictions to the rule form
   * @private
   */
  private applyPublicOfferSetRestrictions(): void {
    const isPublicSelection = this.isPublicRuleSelection();
    if (isPublicSelection) {
      const ruleTypeControl = this.ruleForm.get('ruleType');
      if (ruleTypeControl && ruleTypeControl.value === 'discountRule') {
        ruleTypeControl.setValue('offerSetRule');
      }
    }
    this.updateRuleTypeOptions();
  }


  /**
   * Updates the rule type options based on the selected offer set type
   * @private
   */
  private updateRuleTypeOptions(): void {
    let options = this.ruleTypeOptions;
    if (this.isPublicRuleSelection()) {
      options = this.ruleTypeOptions.filter((option) => option.value === 'offerSetRule');
    }
    this.filteredRuleTypeOptions = options;
  }

  /**
   * @param offerSet
   * @private
   */
  private resolveOfferSetIsPrivate(offerSet: EligibleOfferSetModel): boolean {
    let isPrivate = false;
    if (offerSet) {
      isPrivate = offerSet.isPrivate;
    }
    return isPrivate;
  }

  /**
   * Gets the value of the privacy selection control
   * @returns boolean | null
   * @private
   */
  private getPrivacySelectionValue(): boolean | null {
    let privacyValue: boolean | null = null;
    const privacyControl = this.ruleForm.get('isPrivate');
    if (privacyControl) {
      const controlValue = privacyControl.value;
      if (controlValue !== null && controlValue !== undefined) {
        privacyValue = controlValue;
      }
    }

    return privacyValue;
  }

  /**
   * Retrieves the offer text for the selected offer set.
   * @returns string
   */
  getSelectedOfferSetOfferText(): string {
    let offerText = '';
    const offerSetId = this.ruleForm.get('offerSetId');
    if (offerSetId) {
      const value = offerSetId.value;
      const hasValue = value !== null && value !== undefined;
      if (hasValue && this.offerSetOfferTextMap[value]) {
        offerText = this.offerSetOfferTextMap[value];
      }
    }

    return offerText;
  }

  private buildOfferSetPatch(offerSet: EligibleOfferSetModel): Record<string, unknown> {
    const maxSalePrice = this.resolveMaxSalePrice(offerSet);
    let conditionValue = 'Any';
    if (offerSet.vehicleCondition) {
      conditionValue = offerSet.vehicleCondition;
    }
    const offerSetMake = this.resolveRuleString(offerSet.make);
    const offerSetModel = this.resolveRuleString(offerSet.model);
    const offerSetMakeId = this.findMakeIdByName(offerSetMake);
    let offerSetModelId: number | null = null;
    if (offerSetMakeId !== null) {
      offerSetModelId = this.findModelIdByNameAndMake(offerSetModel, offerSetMakeId);
    }
    const patch = {
      year: offerSet.year,
      make: offerSetMake,
      makeId: offerSetMakeId,
      model: offerSetModel,
      modelId: offerSetModelId,
      trim: this.resolveRuleString(offerSet.trim),
      maxSalePrice,
      vehicleCondition: conditionValue
    };
    return patch;
  }

  private resolveMaxSalePrice(offerSet: EligibleOfferSetModel): number | null {
    let maxSalePrice = null;
    if (offerSet.suggestedMaxSalePrice !== null && offerSet.suggestedMaxSalePrice !== undefined) {
      maxSalePrice = offerSet.suggestedMaxSalePrice;
    }
    if (maxSalePrice === null && offerSet.msrp !== null && offerSet.msrp !== undefined) {
      maxSalePrice = offerSet.msrp;
    }
    if (maxSalePrice === null && offerSet.buyPrice !== null && offerSet.buyPrice !== undefined) {
      maxSalePrice = offerSet.buyPrice;
    }
    return maxSalePrice;
  }

  private filterModelsForMake(makeId: number | null): void {
    this.selectedMakeId = makeId;
    this.modelDropdownDisabled = true;
    this.filteredModels = [this.anyModelOption];
    if (makeId !== null) {
      this.filteredModels = [this.anyModelOption, ...this.models.filter((model) => model.makeId === makeId)];
      this.modelDropdownDisabled = false;
    }
    const modelIdControl = this.ruleForm.get('modelId');
    const modelControl = this.ruleForm.get('model');
    if (modelIdControl && modelControl) {
      const selectedModelId = modelIdControl.value;
      const modelStillAvailable = this.filteredModels.some((model) => model.id === selectedModelId);
      if (!modelStillAvailable) {
        modelIdControl.setValue(null);
        modelControl.setValue('');
      }
    }
  }

  private applyMakeAndModelSelectionFromForm(): void {
    const makeName = this.getControlStringValue('make');
    const makeId = this.findMakeIdByName(makeName);
    this.ruleForm.patchValue({
      makeId
    });
    this.filterModelsForMake(makeId);
    const modelName = this.getControlStringValue('model');
    let modelId = this.findModelIdByNameAndMake(modelName, makeId);
    let resolvedModel = modelName;
    if (modelId === null) {
      resolvedModel = '';
    }
    this.ruleForm.patchValue({
      model: resolvedModel,
      modelId
    });
  }

  private getControlStringValue(controlName: string): string {
    let value = '';
    const control = this.ruleForm.get(controlName);
    if (control) {
      const controlValue = control.value;
      if (controlValue) {
        value = controlValue;
      }
    }
    return value;
  }

  private findMakeIdByName(makeName: string): number | null {
    let makeId: number | null = null;
    if (makeName) {
      this.makes.forEach((make) => {
        if (makeId === null) {
          if (make.displayName === makeName) {
            makeId = make.id as number | null;
          }
        }
      });
    }
    return makeId;
  }

  private findModelIdByNameAndMake(modelName: string, makeId: number | null): number | null {
    let modelId: number | null = null;
    if (modelName) {
      this.models.forEach((model) => {
        if (modelId === null) {
          const hasMakeMatch = makeId === null || model.makeId === makeId;
          if (hasMakeMatch) {
            if (model.name === modelName) {
              modelId = model.id;
            }
          }
        }
      });
    }
    return modelId;
  }

  private getMakeNameById(makeId: number | null): string {
    let makeName = '';
    const hasValue = makeId !== null;
    if (hasValue) {
      const match = this.makes.find((make) => make.id === makeId);
      if (match) {
        makeName = match.displayName;
      }
    }
    return makeName;
  }

  private getRuleTypeFromRule(rule: OfferRuleModel): string {
    let ruleType = 'offerSetRule';
    const hasOfferSet = rule.offerSetId !== null && rule.offerSetId !== undefined;
    if (!hasOfferSet) {
      ruleType = 'discountRule';
    }
    return ruleType;
  }

  private normalizeConditionValue(conditionValue: string | undefined): string {
    let normalized = 'Any';
    if (conditionValue) {
      const upperValue = conditionValue.toUpperCase();
      if (upperValue === 'NEW') {
        normalized = 'New';
      }
      if (upperValue === 'USED') {
        normalized = 'Used';
      }
      if (upperValue === 'CPO') {
        normalized = 'CPO';
      }
      if (upperValue === 'CERTIFIED') {
        normalized = 'CPO';
      }
      if (upperValue === 'ANY') {
        normalized = 'Any';
      }
    }
    return normalized;
  }
}
